import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { StarterData, FeedingEntry } from '@/types/starter';
import type { SyncState } from '@/types/common';
import { CloudStarterRepository } from '@/lib/repositories/CloudStarterRepository';

interface StarterContextValue {
  starter: StarterData | null;
  feedings: FeedingEntry[];
  isLoading: boolean;
  error: string | null;
  syncState: SyncState;
  recordFeeding: (date: string, notes?: string) => Promise<void>;
  createStarter: (date: string, name?: string) => Promise<void>;
  deleteFeeding: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const StarterContext = createContext<StarterContextValue | null>(null);

const DEFAULT_SYNC_STATE: SyncState = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isSyncing: false,
  lastSyncedAt: null,
  lastSyncError: null,
};

interface StarterProviderProps {
  children: ReactNode;
  sessionToken?: string | null;
  apiBaseUrl?: string;
}

export function StarterProvider({ children, sessionToken, apiBaseUrl }: StarterProviderProps) {
  const [starter, setStarter] = useState<StarterData | null>(null);
  const [feedings, setFeedings] = useState<FeedingEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>(DEFAULT_SYNC_STATE);

  // Keep token ref current so the repository always has the latest token
  const tokenRef = useRef(sessionToken);
  tokenRef.current = sessionToken;

  const repositoryRef = useRef<CloudStarterRepository | null>(null);

  const getRepository = useCallback((): CloudStarterRepository => {
    if (!repositoryRef.current && apiBaseUrl) {
      repositoryRef.current = new CloudStarterRepository(
        apiBaseUrl,
        () => tokenRef.current ?? null,
      );
    }
    if (!repositoryRef.current) {
      throw new Error('API not configured');
    }
    return repositoryRef.current;
  }, [apiBaseUrl]);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setSyncState((prev) => ({ ...prev, isSyncing: true }));
      const repo = getRepository();
      const data = await repo.getStarter();
      setStarter(data ?? null);
      if (data) {
        const history = await repo.getFeedingHistory(20);
        setFeedings(history);
      } else {
        setFeedings([]);
      }
      setSyncState((prev) => ({
        ...prev,
        isSyncing: false,
        lastSyncedAt: new Date().toISOString(),
        lastSyncError: null,
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load starter data';
      setError(msg);
      setSyncState((prev) => ({
        ...prev,
        isSyncing: false,
        lastSyncError: msg,
      }));
    } finally {
      setIsLoading(false);
    }
  }, [getRepository]);

  // Fetch from cloud on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setSyncState((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () => setSyncState((prev) => ({ ...prev, isOnline: false }));
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const createStarter = useCallback(
    async (date: string, name?: string) => {
      try {
        setError(null);
        await getRepository().createStarter(date, name);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create starter');
        throw err;
      }
    },
    [getRepository, refresh],
  );

  const recordFeeding = useCallback(
    async (date: string, notes?: string) => {
      try {
        setError(null);
        await getRepository().recordFeeding(date, notes);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to record feeding');
        throw err;
      }
    },
    [getRepository, refresh],
  );

  const deleteFeeding = useCallback(
    async (id: string) => {
      try {
        setError(null);
        await getRepository().deleteFeeding(id);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete feeding');
        throw err;
      }
    },
    [getRepository, refresh],
  );

  return (
    <StarterContext.Provider
      value={{
        starter,
        feedings,
        isLoading,
        error,
        syncState,
        recordFeeding,
        createStarter,
        deleteFeeding,
        refresh,
      }}
    >
      {children}
    </StarterContext.Provider>
  );
}

export function useStarterContext(): StarterContextValue {
  const context = useContext(StarterContext);
  if (!context) {
    throw new Error('useStarterContext must be used within a StarterProvider');
  }
  return context;
}
